import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Check } from "lucide-react";

const planOptions = [
  { id: "solo", name: "Solo", price: "R$ 67/mês", desc: "Até 200 leads" },
  { id: "dupla", name: "Dupla", price: "R$ 117/mês", desc: "Leads ilimitados" },
  { id: "equipe", name: "Equipe", price: "R$ 247/mês", desc: "Até 5 corretores" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    plan: search.get("plan") || "solo"
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Conta criada! Seus 7 dias grátis começaram agora.");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-heading text-2xl font-extrabold text-[#1B3A2F] block mb-6">
          estatebroker<span className="text-[#3B9EFF]">.AI</span>
        </Link>
        <h1 className="font-heading text-3xl font-bold text-[#1C1C19]">Bora vender mais.</h1>
        <p className="text-stone-500 mt-1">7 dias grátis. Sem cartão.</p>

        <form onSubmit={handleSubmit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name" required value={form.name}
              onChange={(e) => update("name", e.target.value)}
              data-testid="register-name-input"
              className="mt-1.5"
              placeholder="Como você assina"
            />
          </div>
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email" type="email" required value={form.email}
              onChange={(e) => update("email", e.target.value)}
              data-testid="register-email-input"
              className="mt-1.5"
              placeholder="seu@email.com"
            />
          </div>
          <div>
            <Label htmlFor="phone">WhatsApp (com DDD)</Label>
            <Input
              id="phone" required value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              data-testid="register-phone-input"
              className="mt-1.5"
              placeholder="(11) 91234-5678"
            />
          </div>
          <div>
            <Label htmlFor="password">Senha (mínimo 6 caracteres)</Label>
            <Input
              id="password" type="password" required value={form.password}
              onChange={(e) => update("password", e.target.value)}
              data-testid="register-password-input"
              className="mt-1.5"
              placeholder="••••••••"
            />
          </div>

          <div>
            <Label className="block mb-2">Plano</Label>
            <RadioGroup
              value={form.plan}
              onValueChange={(v) => update("plan", v)}
              className="space-y-2"
            >
              {planOptions.map((p) => (
                <label
                  key={p.id}
                  htmlFor={`plan-${p.id}`}
                  data-testid={`register-plan-${p.id}`}
                  className={`flex items-center justify-between border rounded-lg p-3 cursor-pointer transition ${
                    form.plan === p.id ? "border-[#1B3A2F] bg-[#1B3A2F]/5" : "border-stone-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem id={`plan-${p.id}`} value={p.id} />
                    <div>
                      <div className="font-semibold text-sm">{p.name}</div>
                      <div className="text-xs text-stone-500">{p.desc}</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-[#1B3A2F]">{p.price}</div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <Button
            type="submit" disabled={loading}
            data-testid="register-submit-btn"
            className="w-full bg-[#1B3A2F] hover:bg-[#244A3C] text-white py-6 rounded-lg"
          >
            {loading ? "Criando..." : "Criar conta e começar grátis"}
          </Button>
          <div className="flex items-center justify-center gap-2 text-xs text-stone-500">
            <Check className="w-3 h-3 text-[#16A34A]" /> 7 dias grátis · sem cartão
          </div>
        </form>

        <p className="mt-6 text-sm text-stone-500 text-center">
          Já tem conta?{" "}
          <Link to="/auth/login" data-testid="goto-login-link" className="text-[#1B3A2F] font-semibold hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
